import { MutationKey, type UseMutationResult, useMutation } from "@tanstack/react-query"
import type { AxiosError, AxiosResponse } from "axios"

import { axiosClient } from "$/http"

import { seconds } from "@/3514/utils"

import Urls from "../../api/urls"

interface IAvailableGuestSessionsMutationParams {
    originalSessionId: number
    swappedSessionId: number
}

interface IAvailableGuestSessionsMutationApiResponse {
    session_id: number
}

type TUseAvailableGuestSessionsMutation = UseMutationResult<
    AxiosResponse<IAvailableGuestSessionsMutationApiResponse>,
    AxiosError
>

async function postAvailableGuestSessionData({
    payload: { originalSessionId: original_session_id, swappedSessionId },
    participantId: participant_id
}: {
    payload: IAvailableGuestSessionsMutationParams
    participantId: number
}): Promise<AxiosResponse<{ session_id: number }>> {
    return axiosClient.post(Urls.postAvailableGuestSessions({ swappedSessionId }), {
        original_session_id,
        participant_id
    })
}

const ESwapSessionMutationKeyBase: string = "GuestSessionSwap" as const

const ESwapSessionMutationKey: (params: {
    originalSessionId: number
    participantId: number
}) => MutationKey = (params: { originalSessionId: number; participantId: number }): MutationKey => [
    ESwapSessionMutationKeyBase,
    `organizationId:${params.originalSessionId}`,
    `participantId:${params.participantId}`
]

function useAvailableGuestSessionsMutation(
    originalSessionId: number,
    participantId: number
): ReturnType<(originalSessionId: number) => TUseAvailableGuestSessionsMutation> {
    return useMutation<
        AxiosResponse<IAvailableGuestSessionsMutationApiResponse>,
        AxiosError,
        IAvailableGuestSessionsMutationParams,
        void
    >({
        mutationKey: ESwapSessionMutationKey({ originalSessionId, participantId }),
        mutationFn: (
            payload: IAvailableGuestSessionsMutationParams
        ): Promise<AxiosResponse<IAvailableGuestSessionsMutationApiResponse>> =>
            postAvailableGuestSessionData({ payload, participantId }),
        throwOnError: false,
        gcTime: 0,
        retry: 1,
        retryDelay: seconds(5)
    })
}

export { useAvailableGuestSessionsMutation, type TUseAvailableGuestSessionsMutation }
export { ESwapSessionMutationKeyBase, ESwapSessionMutationKey }
